import React, { memo, useEffect, useState } from "react";
import { Box, Typography, Checkbox, Collapse } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchDashboardData } from "../../../store/dashboard/action";
import { sortCategoriesByOrder } from "../../../hooks/utilsFn/utilsFn";


const DISCOUNT_OPTIONS = [
  { label: "All Offers", value: "" },
  { label: "0% - 5%", value: "0-5" },
  { label: "5% - 10%", value: "5-10" },
  { label: "10%+", value: "10-plus" },
];

const buildQueryString = (paramsObject) => {
  const parts = [];

  Object.entries(paramsObject).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value) && value.length === 0) return;

    const encoded = Array.isArray(value)
      ? value.map(encodeURIComponent).join("+")
      : encodeURIComponent(String(value));

    parts.push(`${key}=${encoded}`);
  });

  return parts.join("&");
};

const ARRAY_KEYS = ["categorySlug", "brandIds"];

const searchParamsToObject = (searchParams) => {
  const obj = {};
  searchParams.forEach((value, key) => {
    if (ARRAY_KEYS.includes(key)) {
      obj[key] = value
        .split(/[+\s]+/)
        .map(decodeURIComponent)
        .filter(Boolean);
    } else {
      obj[key] = value;
    }
  });
  return obj;
};

const ProductFilterDesktop = memo(({ categorySlug, brandIds, setSearchParams }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const categories = useSelector((state) => state?.Dashboard?.categories);
  const brands = useSelector((state) => state?.Dashboard?.Brands);
  const [categoriesOpen] = useState(true);
  const [discountOpen, setDiscountOpen] = useState(true);
  const [priceOpen, setPriceOpen] = useState(true);
  const [brandOpen, setBrandOpen] = useState(true);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [brandSearch, setBrandSearch] = useState("");

  useEffect(() => {
    if (!categories?.length) {
      dispatch(fetchDashboardData());
    }
  }, [categories, dispatch]);

  const writeParams = (updater) => {
    const current = searchParamsToObject(searchParams);
    updater(current);
    delete current.page;
    delete current.cursorStack;
    setSearchParams(buildQueryString(current), { replace: true });
  };

  const handleToggle = (slug) => {
    const selectedSlug = String(slug);
    writeParams((current) => {
      const existing = current.categorySlug || [];
      const updated = existing.includes(selectedSlug)
        ? existing.filter((s) => s !== selectedSlug)
        : [...existing, selectedSlug];

      if (updated.length > 0) current.categorySlug = updated;
      else delete current.categorySlug;
    });
  };

  const handleAllCategoriesToggle = () => {
    writeParams((current) => {
      delete current.categorySlug;
    });
  };

  const handleBrandToggle = (id) => {
    const selectedId = String(id);
    writeParams((current) => {
      const existing = current.brandIds || [];
      const updated = existing.includes(selectedId)
        ? existing.filter((b) => b !== selectedId)
        : [...existing, selectedId];

      if (updated.length > 0) current.brandIds = updated;
      else delete current.brandIds;
    });
  };

  const handleDiscountChange = (value) => {
    writeParams((current) => {
      if (value) current.discountRange = value;
      else delete current.discountRange;
    });
  };

  const currentDiscount = searchParams.get("discountRange") || "";

  const handlePriceApply = () => {
    writeParams((current) => {
      if (minPrice) current.minPlatformAmount = minPrice;
      else delete current.minPlatformAmount;

      if (maxPrice) current.maxPlatformAmount = maxPrice;
      else delete current.maxPlatformAmount;
    });
  };

  const filteredBrands = (brands || []).filter((brand) =>
    brand?.name?.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const handleReset = () => {
    writeParams((current) => {
      delete current.categorySlug;
      delete current.brandIds;
      delete current.discountRange;
      delete current.minPlatformAmount;
      delete current.maxPlatformAmount;
    });
    setMinPrice("");
    setMaxPrice("");
    setBrandSearch("");
  };


  const CatName = sortCategoriesByOrder(categories);

  const SectionHeader = ({ title, isOpen, onToggle }) => (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
        px: 2,
        py: 1.25,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontFamily: "Be Vietnam Pro",
          fontSize: "15px",
          color: "primary.main",
        }}
      >
        {title}
      </Typography>
      {isOpen ? (
        <ExpandLessIcon sx={{ fontSize: 20, color: "#999" }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 20, color: "#999" }} />
      )}
    </Box>
  );

  const FilterRow = ({ label, count, checked, onClick }) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.75,
        cursor: "pointer",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Be Vietnam Pro",
          fontSize: "13.5px",
          color: "#333333",
          fontWeight: 500,
          textTransform: "capitalize",
        }}
      >
        {label}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {typeof count === "number" && (
          <Typography sx={{ fontSize: "12px", color: "#999" }}>
            {count}
          </Typography>
        )}
        <Checkbox
          size="small"
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={onClick}
          sx={{
            p: 0.5,
            color: "#D9D9D9",
            "&.Mui-checked": { color: "primary.main" },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid #E5E5E5",
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        overflow: "hidden",
        mt: 4.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.75,
          borderBottom: "1px solid #EEEEEE",
        }}
      >
        <Typography
          sx={{
            color: "#1A1A1A",
            fontFamily: "Be Vietnam Pro",
            fontWeight: 700,
            fontSize: "16px",
          }}
        >
          Filters
        </Typography>

        <Typography
          onClick={handleReset}
          sx={{
            color: "primary.main",
            fontFamily: "Be Vietnam Pro",
            fontWeight: 600,
            fontSize: "13px",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Clear All
        </Typography>
      </Box>

      <Box sx={{ borderBottom: "1px solid #EEEEEE" }}>
        <SectionHeader title="Categories" isOpen={categoriesOpen} />
        <Collapse in={categoriesOpen}>
          <Box sx={{ px: 2, pb: 1.5 }}>
            <FilterRow
              label="All Categories"
              checked={categorySlug?.length === 0}
              onClick={handleAllCategoriesToggle}
            />

            {CatName?.map((category) => {
              const isSelected = categorySlug?.includes(String(category?.slug));
              return (
                <FilterRow
                  key={category?.id}
                  label={category?.name}
                  count={category?.productCount}
                  checked={isSelected}
                  onClick={() => handleToggle(category?.slug)}
                />
              );
            })}
          </Box>
        </Collapse>
      </Box>

      {/* commented-out sections stay as-is */}
    </Box>
  );
});

export default ProductFilterDesktop;